/**
 * saveBlob — hand a Blob to the browser as a file download.
 *
 * ONE RULE, AND IT IS THE WHOLE REASON THIS FILE EXISTS:
 * never revoke the object URL in the same tick as the click.
 *
 * WHAT WENT WRONG (DL-SAFARI-1)
 *
 * Three places built a download by hand and all three ended:
 *
 *     link.click();
 *     document.body.removeChild(link);
 *     URL.revokeObjectURL(blobUrl);   // <- same tick
 *
 * Chrome and Firefox begin reading the blob synchronously inside
 * `click()`, so by the time `revokeObjectURL` runs they already hold
 * the data and the download completes. **Safari does not.** It queues
 * the download and reads the URL afterwards, so revoking immediately
 * hands it a URL that no longer resolves — and Safari reports nothing
 * at all. No error, no console warning, no failed download in the
 * download manager. It simply never appears.
 *
 * Which is exactly what a studio's client sees: the toast says
 * "Downloading full resolution", everything up to the click worked,
 * and no file ever arrives. On Chrome the same gallery is fine, which
 * is what makes it look like a Safari problem rather than ours.
 *
 * `downloadZip.ts` already knew this — `blobToImageBitmap` delays its
 * revoke by 5s with a comment saying immediate revoke "would race" —
 * but the download path 280 lines above it did not.
 *
 * WHY 60 SECONDS
 *
 * Long enough for Safari to finish copying a multi-gigabyte wedding
 * ZIP on a phone over a hotel connection, which is the worst case
 * this has to survive. The blob is already in memory at this point;
 * keeping its URL alive only delays collection, and `pagehide` drops
 * it sooner if the client closes the tab.
 */

/** Generous enough for a slow phone to copy a large archive. */
const REVOKE_AFTER_MS = 60_000;

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  revokeLater(url);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  // Safari will not follow a detached anchor's click, so it has to be
  // in the document — briefly.
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/** Same contract for a URL the caller already has (not a blob). */
export function saveUrl(href: string, filename: string): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function revokeLater(url: string): void {
  let done = false;
  const revoke = () => {
    if (done) return;
    done = true;
    window.removeEventListener('pagehide', revoke);
    URL.revokeObjectURL(url);
  };
  window.setTimeout(revoke, REVOKE_AFTER_MS);
  // A client who saves and immediately closes the tab shouldn't hold a
  // multi-GB blob for the rest of the minute.
  window.addEventListener('pagehide', revoke, { once: true });
}
